import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/config";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Clock, DollarSign, Save, Plus, Trash2, Sparkles, ChevronDown, ChevronUp } from "lucide-react";

const TIER_OPTIONS = ["free", "workshop", "course", "membership", "cohort"];

function emptyDraft() {
  return {
    title: "",
    description: "",
    duration_min: 30,
    price_dollars: 0,
    tier_required: "free",
    sort_order: 100,
    active: true,
    is_default_returning: false,
  };
}

function toDraft(st) {
  return {
    title: st.title || "",
    description: st.description || "",
    duration_min: Number(st.duration_min ?? 30),
    price_dollars: Number(((st.price_cents ?? 0) / 100).toFixed(2)),
    tier_required: st.tier_required || "free",
    sort_order: Number(st.sort_order ?? 100),
    active: !!st.active,
    is_default_returning: !!st.is_default_returning,
  };
}

function draftToPayload(d) {
  return {
    title: (d.title || "").trim(),
    description: (d.description || "").trim(),
    duration_min: Math.max(15, Math.min(180, parseInt(d.duration_min, 10) || 30)),
    price_cents: Math.max(0, Math.round(Number(d.price_dollars || 0) * 100)),
    tier_required: d.tier_required || "free",
    sort_order: parseInt(d.sort_order, 10) || 100,
    active: !!d.active,
    is_default_returning: !!d.is_default_returning,
  };
}

export default function SessionTypesEditor({ compact = false, title = "Session types", subtitle }) {
  const [types, setTypes] = useState([]);
  const [drafts, setDrafts] = useState({}); // by session_type_id
  const [savingId, setSavingId] = useState("");
  const [expanded, setExpanded] = useState({});
  const [showNew, setShowNew] = useState(false);
  const [newDraft, setNewDraft] = useState(emptyDraft());
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/booking/session-types");
      const list = Array.isArray(data) ? data : [];
      setTypes(list);
      const d = {};
      list.forEach((s) => { d[s.session_type_id] = toDraft(s); });
      setDrafts(d);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Failed to load session types");
    }
  };
  useEffect(() => { load(); }, []);

  const isDirty = (st) => {
    const d = drafts[st.session_type_id];
    if (!d) return false;
    const orig = toDraft(st);
    return JSON.stringify(d) !== JSON.stringify(orig);
  };

  const setDraft = (id, patch) => setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  const save = async (st) => {
    const d = drafts[st.session_type_id];
    if (!d) return;
    if (!d.title.trim()) { toast.error("Title is required"); return; }
    setSavingId(st.session_type_id);
    try {
      await api.patch(`/admin/booking/session-types/${st.session_type_id}`, draftToPayload(d));
      toast.success(`Saved "${d.title}"`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Save failed");
    } finally {
      setSavingId("");
    }
  };

  const remove = async (st) => {
    if (!window.confirm(`Delete "${st.title}"? Existing bookings are unaffected, but the public page will hide it.`)) return;
    try {
      await api.delete(`/admin/booking/session-types/${st.session_type_id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Delete failed");
    }
  };

  const create = async () => {
    if (!newDraft.title.trim()) { toast.error("Title is required"); return; }
    setCreating(true);
    try {
      await api.post("/admin/booking/session-types", draftToPayload(newDraft));
      toast.success("Session type created");
      setNewDraft(emptyDraft());
      setShowNew(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Create failed");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="glass rounded-2xl p-5 md:p-6" data-testid="session-types-editor">
      <div className="flex items-start gap-4 flex-wrap">
        <div className="w-11 h-11 rounded-lg flex items-center justify-center shrink-0 bg-[#FF8A00]/15 text-[#FF8A00]">
          <Clock size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-xl">{title}</div>
          <div className="text-sm text-white/55 mt-1">
            {subtitle || "Set the price, length and title for each type of 1-on-1 session with Dr. Slava. Changes appear instantly on the public Book a Session page."}
          </div>
        </div>
        <Button
          onClick={() => setShowNew((v) => !v)}
          data-testid="session-type-new-btn"
          className="rounded-full bg-gradient-to-r from-[#FF8A00] to-[#E52E71]"
        >
          <Plus size={14} className="mr-1.5" /> New type
        </Button>
      </div>

      {showNew && (
        <div className="mt-5 rounded-xl border border-dashed border-white/15 p-4 space-y-3" data-testid="session-type-new-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <Label className="text-xs text-white/60">Title</Label>
              <Input value={newDraft.title}
                onChange={(e) => setNewDraft({ ...newDraft, title: e.target.value })}
                placeholder="e.g. Deep-Dive Peptide Consultation"
                data-testid="session-type-new-title"
                className="bg-[#0F0F0F] border-white/10 mt-1" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs text-white/60">Description</Label>
              <Textarea value={newDraft.description}
                onChange={(e) => setNewDraft({ ...newDraft, description: e.target.value })}
                rows={2}
                placeholder="What members get in this session..."
                data-testid="session-type-new-description"
                className="bg-[#0F0F0F] border-white/10 mt-1" />
            </div>
            <div>
              <Label className="text-xs text-white/60">Duration (minutes)</Label>
              <Input type="number" min={15} max={180} step={5}
                value={newDraft.duration_min}
                onChange={(e) => setNewDraft({ ...newDraft, duration_min: e.target.value })}
                data-testid="session-type-new-duration"
                className="bg-[#0F0F0F] border-white/10 mt-1" />
            </div>
            <div>
              <Label className="text-xs text-white/60">Price (USD)</Label>
              <div className="relative mt-1">
                <DollarSign size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <Input type="number" min={0} step={1}
                  value={newDraft.price_dollars}
                  onChange={(e) => setNewDraft({ ...newDraft, price_dollars: e.target.value })}
                  data-testid="session-type-new-price"
                  className="bg-[#0F0F0F] border-white/10 pl-8" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-white/60">Minimum tier</Label>
              <Select value={newDraft.tier_required}
                onValueChange={(v) => setNewDraft({ ...newDraft, tier_required: v })}>
                <SelectTrigger className="bg-[#0F0F0F] border-white/10 mt-1" data-testid="session-type-new-tier">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/10">
                  {TIER_OPTIONS.map((t) => (
                    <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-white/60">Sort order</Label>
              <Input type="number" step={10}
                value={newDraft.sort_order}
                onChange={(e) => setNewDraft({ ...newDraft, sort_order: e.target.value })}
                data-testid="session-type-new-sort"
                className="bg-[#0F0F0F] border-white/10 mt-1" />
            </div>
          </div>
          <div className="flex items-center justify-between flex-wrap gap-3 pt-1">
            <div className="flex flex-wrap items-center gap-4">
              <label className="flex items-center gap-2 text-sm text-white/70">
                <Switch checked={newDraft.active}
                  onCheckedChange={(v) => setNewDraft({ ...newDraft, active: v })}
                  data-testid="session-type-new-active" />
                Active (visible on public page)
              </label>
              <label className="flex items-center gap-2 text-sm text-white/70"
                title="Pre-selected on /book-session for returning members with a saved intake">
                <Switch checked={newDraft.is_default_returning}
                  onCheckedChange={(v) => setNewDraft({ ...newDraft, is_default_returning: v })}
                  data-testid="session-type-new-default-returning" />
                Default for returning members
              </label>
            </div>
            <div className="flex gap-2">
              <Button variant="outline"
                onClick={() => { setShowNew(false); setNewDraft(emptyDraft()); }}
                className="rounded-full bg-transparent border-white/10">Cancel</Button>
              <Button onClick={create} disabled={creating}
                data-testid="session-type-new-save"
                className="rounded-full bg-gradient-to-r from-[#FF8A00] to-[#E52E71]">
                <Save size={13} className="mr-1.5" /> {creating ? "Creating..." : "Create"}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="mt-5 space-y-3">
        {types.length === 0 && (
          <div className="rounded-xl border border-white/5 bg-[#0F0F0F] p-6 text-center text-sm text-white/40 flex items-center justify-center gap-2">
            <Sparkles size={14} /> No session types yet. Create your first one above.
          </div>
        )}
        {types.map((st) => {
          const d = drafts[st.session_type_id] || toDraft(st);
          const dirty = isDirty(st);
          const isOpen = compact ? !!expanded[st.session_type_id] : true;
          return (
            <div key={st.session_type_id}
              data-testid={`session-type-row-${st.session_type_id}`}
              className={`rounded-xl border ${dirty ? "border-[#FF8A00]/40" : "border-white/5"} bg-[#0F0F0F]`}>
              <button
                type="button"
                onClick={() => compact && setExpanded({ ...expanded, [st.session_type_id]: !isOpen })}
                className="w-full flex items-center gap-3 p-4 text-left"
                data-testid={`session-type-toggle-${st.session_type_id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="font-display text-base flex items-center gap-2">
                    {st.title}
                    {st.is_default_returning && (
                      <span data-testid={`session-type-default-badge-${st.session_type_id}`}
                        className="text-[10px] tracking-[0.2em] uppercase text-[#FF8A00] border border-[#FF8A00]/40 rounded-full px-2 py-0.5">
                        Default (returning)
                      </span>
                    )}
                    {!st.active && <span className="text-[10px] tracking-[0.2em] uppercase text-white/40 border border-white/10 rounded-full px-2 py-0.5">Hidden</span>}
                    {dirty && <span className="text-[10px] tracking-[0.2em] uppercase text-[#FF8A00]">Unsaved</span>}
                  </div>
                  <div className="text-xs text-white/50 mt-0.5">
                    ${(st.price_cents / 100).toFixed(0)} · {st.duration_min} min · tier: <span className="capitalize">{st.tier_required}</span>
                  </div>
                </div>
                {compact && (isOpen ? <ChevronUp size={16} className="text-white/40" /> : <ChevronDown size={16} className="text-white/40" />)}
              </button>

              {isOpen && (
                <div className="px-4 pb-4 pt-1 space-y-3 border-t border-white/5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-3">
                    <div className="md:col-span-2">
                      <Label className="text-xs text-white/60">Title</Label>
                      <Input value={d.title}
                        onChange={(e) => setDraft(st.session_type_id, { title: e.target.value })}
                        data-testid={`session-type-title-${st.session_type_id}`}
                        className="bg-[#0A0A0A] border-white/10 mt-1" />
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-xs text-white/60">Description</Label>
                      <Textarea value={d.description} rows={2}
                        onChange={(e) => setDraft(st.session_type_id, { description: e.target.value })}
                        data-testid={`session-type-description-${st.session_type_id}`}
                        className="bg-[#0A0A0A] border-white/10 mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs text-white/60">Duration (minutes)</Label>
                      <Input type="number" min={15} max={180} step={5}
                        value={d.duration_min}
                        onChange={(e) => setDraft(st.session_type_id, { duration_min: e.target.value })}
                        data-testid={`session-type-duration-${st.session_type_id}`}
                        className="bg-[#0A0A0A] border-white/10 mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs text-white/60">Price (USD)</Label>
                      <div className="relative mt-1">
                        <DollarSign size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                        <Input type="number" min={0} step={1}
                          value={d.price_dollars}
                          onChange={(e) => setDraft(st.session_type_id, { price_dollars: e.target.value })}
                          data-testid={`session-type-price-${st.session_type_id}`}
                          className="bg-[#0A0A0A] border-white/10 pl-8" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-white/60">Minimum tier</Label>
                      <Select value={d.tier_required}
                        onValueChange={(v) => setDraft(st.session_type_id, { tier_required: v })}>
                        <SelectTrigger className="bg-[#0A0A0A] border-white/10 mt-1"
                          data-testid={`session-type-tier-${st.session_type_id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0A0A0A] border-white/10">
                          {TIER_OPTIONS.map((t) => (
                            <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-white/60">Sort order</Label>
                      <Input type="number" step={10}
                        value={d.sort_order}
                        onChange={(e) => setDraft(st.session_type_id, { sort_order: e.target.value })}
                        data-testid={`session-type-sort-${st.session_type_id}`}
                        className="bg-[#0A0A0A] border-white/10 mt-1" />
                    </div>
                  </div>
                  <div className="flex items-center justify-between flex-wrap gap-3 pt-1">
                    <div className="flex flex-wrap items-center gap-4">
                      <label className="flex items-center gap-2 text-sm text-white/70">
                        <Switch checked={!!d.active}
                          onCheckedChange={(v) => setDraft(st.session_type_id, { active: v })}
                          data-testid={`session-type-active-${st.session_type_id}`} />
                        Active (visible on public page)
                      </label>
                      <label className="flex items-center gap-2 text-sm text-white/70"
                        title="Pre-selected on /book-session for returning members with a saved intake">
                        <Switch checked={!!d.is_default_returning}
                          onCheckedChange={(v) => setDraft(st.session_type_id, { is_default_returning: v })}
                          data-testid={`session-type-default-returning-${st.session_type_id}`} />
                        Default for returning members
                      </label>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline"
                        onClick={() => remove(st)}
                        data-testid={`session-type-delete-${st.session_type_id}`}
                        className="rounded-full bg-transparent border-white/10 text-[#E52E71]">
                        <Trash2 size={13} className="mr-1.5" /> Delete
                      </Button>
                      <Button
                        onClick={() => save(st)}
                        disabled={!dirty || savingId === st.session_type_id}
                        data-testid={`session-type-save-${st.session_type_id}`}
                        className="rounded-full bg-gradient-to-r from-[#FF8A00] to-[#E52E71] disabled:opacity-40">
                        <Save size={13} className="mr-1.5" />
                        {savingId === st.session_type_id ? "Saving..." : dirty ? "Save changes" : "Saved"}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
